/**
 * Gamification types for AI Readiness Bar
 * Defines the data structures for tracking user progress and achievements
 */

export interface UserGamification {
  userId: string;
  totalXP: number;
  readinessPercentage: number;
  completedTasks: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface GamificationTask {
  id: string;
  category: TaskCategory;
  title: string;
  description: string;
  xpReward: number;
  isRepeatable: boolean;
  order: number;
}

export type TaskCategory = 
  | 'onboarding'
  | 'foundation'
  | 'learning'
  | 'habit'
  | 'mastery'
  | 'engagement'
  | 'advocacy'
  | 'community';

export interface TaskCompletion {
  taskId: string;
  userId: string;
  completedAt: Date;
  xpAwarded: number;
}

export interface ReadinessLevel {
  min: number;
  max: number;
  color: string;
  label: string;
  emoji: string;
}

export interface HintMessage {
  id: string;
  message: string;
  actionText: string;
  actionUrl: string;
  condition: (user: UserGamification) => boolean;
  priority: number;
}

// Constants for XP calculation
export const XP_TARGET = 1000; // Total XP needed to reach 100%

// Readiness levels based on percentage
export const READINESS_LEVELS: ReadinessLevel[] = [
  {
    min: 0,
    max: 30,
    color: 'from-red-500 to-orange-500',
    label: 'Beginner',
    emoji: '⚠️',
  },
  {
    min: 30,
    max: 60,
    color: 'from-yellow-400 to-yellow-500',
    label: 'Improving',
    emoji: '🟡',
  },
  {
    min: 60,
    max: 90,
    color: 'from-green-400 to-green-500',
    label: 'Confident',
    emoji: '✅',
  },
  {
    min: 90,
    max: 100,
    color: 'from-blue-500 via-purple-500 to-purple-600',
    label: 'Expert',
    emoji: '💎',
  },
];

// Default tasks configuration
export const DEFAULT_TASKS: GamificationTask[] = [
  {
    id: 'register_account',
    category: 'onboarding',
    title: 'Register for a free account',
    description: 'Create your trAInedup.ai account',
    xpReward: 100,
    isRepeatable: false,
    order: 1,
  },
  {
    id: 'complete_profile',
    category: 'onboarding',
    title: 'Complete your profile',
    description: 'Add your personal information',
    xpReward: 30,
    isRepeatable: false,
    order: 2,
  },
  {
    id: 'take_skills_assessment',
    category: 'foundation',
    title: 'Take the Skills Assessment',
    description: 'Complete the AI skills assessment',
    xpReward: 100,
    isRepeatable: false,
    order: 3,
  },
  {
    id: 'complete_first_free_course',
    category: 'foundation',
    title: 'Complete the first free course',
    description: 'Finish your first free course',
    xpReward: 50,
    isRepeatable: false,
    order: 4,
  },
  {
    id: 'upgrade_account',
    category: 'engagement',
    title: 'Upgrade to a paid account',
    description: 'Subscribe to a paid plan',
    xpReward: 100,
    isRepeatable: false,
    order: 5,
  },
  {
    id: 'complete_first_paid_course',
    category: 'learning',
    title: 'Complete your first paid course',
    description: 'Finish your first paid course',
    xpReward: 20,
    isRepeatable: false,
    order: 6,
  },
  {
    id: 'complete_5_paid_courses',
    category: 'learning',
    title: 'Complete 5 paid courses',
    description: 'Finish 5 paid courses',
    xpReward: 100,
    isRepeatable: false,
    order: 7,
  },
  {
    id: 'login_3_days_streak',
    category: 'habit',
    title: 'Log in 3 days in a row',
    description: 'Build a 3-day login streak',
    xpReward: 30,
    isRepeatable: false,
    order: 8,
  },
  {
    id: 'login_5_days_streak',
    category: 'habit',
    title: 'Log in 5 days in a row',
    description: 'Build a 5-day login streak',
    xpReward: 50,
    isRepeatable: false,
    order: 9,
  },
  {
    id: 'finish_10_courses',
    category: 'mastery',
    title: 'Finish 10 courses',
    description: 'Complete 10 courses total',
    xpReward: 100,
    isRepeatable: false,
    order: 10,
  },
  {
    id: 'become_affiliate',
    category: 'advocacy',
    title: 'Become an affiliate',
    description: 'Register as an affiliate and earn recurring income',
    xpReward: 30,
    isRepeatable: false,
    order: 11,
  },
  {
    id: 'join_discord',
    category: 'community',
    title: 'Join the Discord community',
    description: 'Connect with other learners',
    xpReward: 20,
    isRepeatable: false,
    order: 12,
  },
];

// Tasks for 12-Month Package Users
export const TWELVE_MONTH_PACKAGE_TASKS: GamificationTask[] = [
  {
    id: 'register_account',
    category: 'onboarding',
    title: 'Register for a free account',
    description: 'Create your trAInedup.ai account',
    xpReward: 100,
    isRepeatable: false,
    order: 1,
  },
  {
    id: 'complete_profile',
    category: 'onboarding',
    title: 'Complete your profile',
    description: 'Add your personal information',
    xpReward: 30,
    isRepeatable: false,
    order: 2,
  },
  {
    id: 'complete_first_free_course',
    category: 'foundation',
    title: 'Complete the first free course',
    description: 'Finish your first free course',
    xpReward: 50,
    isRepeatable: false,
    order: 3,
  },
  {
    id: 'complete_3_arts_creative',
    category: 'learning',
    title: 'Complete 3 Arts and Creative courses',
    description: 'Finish 3 courses in the Arts and Creative category',
    xpReward: 150,
    isRepeatable: false,
    order: 4,
  },
  {
    id: 'complete_3_business_entrepreneur',
    category: 'learning',
    title: 'Complete 3 Business and Entrepreneur courses',
    description: 'Finish 3 courses in the Business and Entrepreneur category',
    xpReward: 150,
    isRepeatable: false,
    order: 5,
  },
  {
    id: 'complete_3_ai_tools',
    category: 'learning',
    title: 'Complete 3 AI Tools courses',
    description: 'Finish 3 courses in the AI Tools category',
    xpReward: 150,
    isRepeatable: false,
    order: 6,
  },
  {
    id: 'complete_5_paid_courses',
    category: 'learning',
    title: 'Complete 5 paid courses',
    description: 'Finish 5 paid courses',
    xpReward: 100,
    isRepeatable: false,
    order: 7,
  },
  {
    id: 'login_3_days_streak',
    category: 'habit',
    title: 'Log in 3 days in a row',
    description: 'Build a 3-day login streak',
    xpReward: 30,
    isRepeatable: false,
    order: 8,
  },
  {
    id: 'login_5_days_streak',
    category: 'habit',
    title: 'Log in 5 days in a row',
    description: 'Build a 5-day login streak',
    xpReward: 50,
    isRepeatable: false,
    order: 9,
  },
  {
    id: 'finish_10_courses',
    category: 'mastery',
    title: 'Finish 10 courses',
    description: 'Complete 10 courses total',
    xpReward: 100,
    isRepeatable: false,
    order: 10,
  },
  {
    id: 'become_affiliate',
    category: 'advocacy',
    title: 'Become an affiliate',
    description: 'Register as an affiliate and earn recurring income',
    xpReward: 30,
    isRepeatable: false,
    order: 11,
  },
  {
    id: 'join_discord',
    category: 'community',
    title: 'Join the Discord community',
    description: 'Connect with other learners',
    xpReward: 20,
    isRepeatable: false,
    order: 12,
  },
];
